import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import WorldMap from "../components/WorldMap";
import CountdownTimer from "../components/CountdownTimer";
import {
  Users,
  FolderKanban,
  DollarSign,
  Shield,
  TrendingUp,
  Plus,
  CheckCircle,
  Clock,
  BarChart3,
  FileCheck,
  Eye,
  Rocket
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createProjectModal, setCreateProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    industry: "",
    token_symbol: "",
    location: "",
    roi_percentage: 15,
    launch_date: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, investorsRes, projectsRes, mapRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/admin/investors`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/map-data`)
      ]);
      setAnalytics(analyticsRes.data);
      setInvestors(investorsRes.data);
      setProjects(projectsRes.data);
      setMapData(mapRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  const handleCreateProject = async () => {
    try {
      const projectData = {
        ...newProject,
        image_url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
        coordinates: { lat: -26.0, lng: 28.0 }
      };
      await axios.post(`${API}/admin/projects`, projectData);
      toast.success("Project created successfully!");
      setCreateProjectModal(false);
      setNewProject({
        name: "",
        description: "",
        industry: "",
        token_symbol: "",
        location: "",
        roi_percentage: 15,
        launch_date: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const industries = [
    "Mining", "Real Estate", "Mixed Development", "Solar Energy",
    "Agriculture", "Infrastructure", "Technology", "Tourism & Hospitality"
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage platform operations and document compliance</p>
        </div>
        <Button
          onClick={() => setCreateProjectModal(true)}
          className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
          data-testid="create-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Token Project
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-total-projects">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Projects</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{analytics?.total_projects || 0}</p>
                <p className="text-sm text-[#3B82F6] mt-2 flex items-center gap-1">
                  <Rocket className="w-3 h-3" />
                  {analytics?.launching_projects || 0} launching soon
                </p>
              </div>
              <div className="w-12 h-12 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-avg-score">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Average Score</p>
                <p className={`text-3xl font-bold mt-2 ${getScoreColor(analytics?.average_score || 0)}`}>
                  {analytics?.average_score || 0}/1000
                </p>
                <p className="text-sm text-slate-500 mt-2">Document compliance</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-token-price">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Token Price</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  ${analytics?.token_price || 986}
                </p>
                <p className="text-sm text-slate-500 mt-2">Per token</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-token-supply">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Tokens Per Project</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {(analytics?.token_supply_per_project || 100000).toLocaleString()}
                </p>
                <p className="text-sm text-slate-500 mt-2">Standard allocation</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* World Map */}
      {mapData && <WorldMap projects={mapData.projects} />}

      {/* Launch Countdowns */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Upcoming Launches</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {projects.map((project) => (
              <CountdownTimer
                key={project.id}
                launchDate={project.launch_date}
                projectName={project.name}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs Section */}
      <Tabs defaultValue="projects" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger 
            value="projects" 
            className="text-slate-900 font-semibold data-[state=active]:bg-[#0F2744] data-[state=active]:text-white px-6"
            data-testid="tab-projects"
          >
            Projects
          </TabsTrigger>
          <TabsTrigger 
            value="documents" 
            className="text-slate-900 font-semibold data-[state=active]:bg-[#0F2744] data-[state=active]:text-white px-6"
            data-testid="tab-documents"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger 
            value="investors" 
            className="text-slate-900 font-semibold data-[state=active]:bg-[#0F2744] data-[state=active]:text-white px-6"
            data-testid="tab-investors"
          >
            Investors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">All Projects</CardTitle>
              <span className="text-sm text-slate-500">{projects.length} total</span>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Project</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Industry</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Score</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Launch Date</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => {
                      const score = project.score_data?.score || 0;
                      return (
                        <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-row-${project.id}`}>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={project.image_url}
                                alt={project.name}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                              <div>
                                <p className="font-medium text-slate-900">{project.name}</p>
                                <p className="text-xs text-slate-500 font-mono">{project.token_symbol}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="industry-tag">{project.industry}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`font-bold ${getScoreColor(score)}`}>
                              {score}/1000
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="bg-[#0F2744] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <Rocket className="w-3 h-3" />
                              Launching
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm text-slate-600">
                              {new Date(project.launch_date).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button variant="ghost" size="sm" data-testid={`view-project-${project.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Document Compliance Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {projects.map((project) => {
                  const scoreData = project.score_data || {};
                  const score = scoreData.score || 0;
                  
                  return (
                    <div key={project.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={project.image_url}
                            alt={project.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div>
                            <h4 className="font-semibold text-slate-900">{project.name}</h4>
                            <p className="text-sm text-slate-500">{project.industry}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}/1000</p>
                          <p className="text-sm text-slate-500">{scoreData.completion_percentage || 0}% complete</p>
                        </div>
                      </div>
                      
                      <Progress value={(score / 1000) * 100} className="h-2 mb-4" />
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-2 bg-emerald-50 rounded-lg">
                          <div className="flex items-center justify-center gap-1 text-emerald-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-bold">{scoreData.approved_documents || 0}</span>
                          </div>
                          <p className="text-xs text-emerald-600">Approved</p>
                        </div>
                        <div className="text-center p-2 bg-amber-50 rounded-lg">
                          <div className="flex items-center justify-center gap-1 text-amber-600">
                            <Clock className="w-4 h-4" />
                            <span className="font-bold">{scoreData.pending_documents || 0}</span>
                          </div>
                          <p className="text-xs text-amber-600">Pending</p>
                        </div>
                        <div className="text-center p-2 bg-slate-100 rounded-lg">
                          <div className="flex items-center justify-center gap-1 text-slate-500">
                            <FileCheck className="w-4 h-4" />
                            <span className="font-bold">{scoreData.missing_documents || 0}</span>
                          </div>
                          <p className="text-xs text-slate-500">Missing</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investors">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Registered Investors</CardTitle>
              <span className="text-sm text-slate-500">{investors.length} total</span>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Investor</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Wallet</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">KYC Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investors.map((investor) => (
                      <tr key={investor.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`investor-row-${investor.id}`}>
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{investor.name}</p>
                            <p className="text-sm text-slate-500">{investor.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="wallet-address">
                            {investor.wallet_address?.slice(0, 8)}...{investor.wallet_address?.slice(-6)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {investor.kyc_verified ? (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-4 h-4" />
                              Verified
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Clock className="w-4 h-4" />
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Project Modal */}
      <Dialog open={createProjectModal} onOpenChange={setCreateProjectModal}>
        <DialogContent className="max-w-lg" data-testid="create-project-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Create New Token Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-900 font-semibold">Project Name</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Enter project name"
                className="border-slate-300"
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-900 font-semibold">Description</Label>
              <Input
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief project description"
                className="border-slate-300"
                data-testid="project-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-900 font-semibold">Industry</Label>
                <Select
                  value={newProject.industry}
                  onValueChange={(value) => setNewProject({ ...newProject, industry: value })}
                >
                  <SelectTrigger className="border-slate-300" data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-900 font-semibold">Token Symbol</Label>
                <Input
                  value={newProject.token_symbol}
                  onChange={(e) => setNewProject({ ...newProject, token_symbol: e.target.value.toUpperCase() })}
                  placeholder="e.g., ABC"
                  maxLength={5}
                  className="border-slate-300"
                  data-testid="token-symbol-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-900 font-semibold">Expected ROI (%)</Label>
                <Input
                  type="number"
                  value={newProject.roi_percentage}
                  onChange={(e) => setNewProject({ ...newProject, roi_percentage: parseFloat(e.target.value) })}
                  className="border-slate-300"
                  data-testid="roi-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-900 font-semibold">Launch Date</Label>
                <Input
                  type="date"
                  value={newProject.launch_date}
                  onChange={(e) => setNewProject({ ...newProject, launch_date: e.target.value })}
                  className="border-slate-300"
                  data-testid="launch-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-900 font-semibold">Location</Label>
              <Input
                value={newProject.location}
                onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                placeholder="City, Province, South Africa"
                className="border-slate-300"
                data-testid="location-input"
              />
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-600">
                <strong className="text-slate-900">Token Configuration:</strong><br />
                • Token Supply: 100,000 tokens<br />
                • Token Price: $986 per token<br />
                • Funding Goal: $98,600,000
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateProjectModal(false)} className="text-slate-900">
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
              data-testid="submit-create-project"
            >
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
